import { useEffect, useRef, useState } from 'react';

// ── useReveal ─────────────────────────────────────────────────────────
// Adds `.in` class to elements with `.reveal` once they scroll into view.
// Content is visible by default (CSS); `.in` triggers the entrance animation.
export function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('.reveal'));
    if (!els.length) return;

    // Synchronously reveal anything already in (or near) the viewport on mount.
    // This avoids a frame of "frozen at start state" before IO's async callback fires.
    const checkAll = () => {
      els.forEach((el) => {
        if (el.classList.contains('in')) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.95 && r.bottom > 0) el.classList.add('in');
      });
    };
    checkAll();

    let io: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('in');
              io!.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12 },
      );
      els.forEach((el) => { if (!el.classList.contains('in')) io!.observe(el); });
    }

    const onScroll = () => checkAll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Final safety net for content that's never scrolled to (e.g. user lands deep)
    const t = window.setTimeout(() => els.forEach((el) => el.classList.add('in')), 2000);

    return () => {
      io?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.clearTimeout(t);
    };
  }, []);
}

// ── useInView ─────────────────────────────────────────────────────────
// Returns a ref + boolean for whether the target element is in view.
export function useInView<T extends HTMLElement>(threshold = 0.3): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || !('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ── useTheme ──────────────────────────────────────────────────────────
type Theme = 'light' | 'dark';
export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem('lp-theme');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch { /* noop */ }
    return 'light';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('lp-theme', theme); } catch { /* noop */ }
  }, [theme]);
  const toggle = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  return [theme, toggle];
}

// ── useCountUp ────────────────────────────────────────────────────────
// Animates from 0 to `to` over `duration` ms when `trigger` becomes true.
// Returns the formatted string, e.g. "23%" or "₹4.2L" or "-34%".
export function useCountUp(
  to: number,
  trigger: boolean,
  opts: { duration?: number; decimals?: number; prefix?: string; suffix?: string } = {},
): string {
  const { duration = 1400, decimals = 0, prefix = '', suffix = '' } = opts;
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!trigger) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, trigger, duration]);

  const sign = to < 0 ? '-' : '';
  const abs = Math.abs(val);
  const num = decimals ? abs.toFixed(decimals) : Math.round(abs).toString();
  return `${sign}${prefix}${num}${suffix}`;
}

// ── smoothScrollTo ────────────────────────────────────────────────────
export function smoothScrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
}
